import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useBooking } from "./BookingContext";

const LINKS = [
  { label: "The Formula", href: "#formula" },
  { label: "Projects", href: "#projects" },
  { label: "Journal", href: "#journal" },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Nav() {
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 z-50 w-full transition-all duration-700 ${
          scrolled ? "py-4 bg-warm/80 backdrop-blur-md border-b border-ink/10" : "py-7 bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 md:px-12">
          <button
            data-testid="nav-logo"
            onClick={() => go("#top")}
            className={`font-serif text-2xl md:text-[1.7rem] leading-none tracking-tight whitespace-nowrap transition-colors duration-500 ${
              scrolled ? "text-ink" : "text-warm"
            }`}
          >
            Studio of EMA<span className="text-copper">.</span>
          </button>

          <nav
            className={`hidden md:flex items-center gap-10 text-[0.8rem] uppercase tracking-[0.18em] transition-colors duration-500 ${
              scrolled ? "text-ink" : "text-warm"
            }`}
          >
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => go(l.href)}
                className="link-underline"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-strategy-call"
              onClick={openBooking}
              className={`link-underline font-semibold ${scrolled ? "text-forest" : "text-champagne"}`}
            >
              Strategy Call
            </button>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(true)}
            className={`md:hidden flex flex-col gap-[6px] ${scrolled ? "text-ink" : "text-warm"}`}
            aria-label="Open menu"
          >
            <span className="block h-px w-7 bg-current" />
            <span className="block h-px w-7 bg-current" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-charcoal text-warm"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="flex items-center justify-between px-6 py-7">
              <span className="font-serif text-2xl">Studio of EMA<span className="text-copper">.</span></span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="text-[0.8rem] uppercase tracking-[0.2em]"
              >
                Close
              </button>
            </div>
            <div className="flex flex-col px-6 pt-10">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="border-b border-warm/15 py-6 text-left font-serif text-4xl"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.08, duration: 0.6, ease: EASE }}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-menu-strategy-call"
                onClick={() => {
                  setMenuOpen(false);
                  openBooking();
                }}
                className="mt-10 bg-forest px-8 py-5 text-[0.8rem] uppercase tracking-[0.2em]"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
              >
                Book Your Strategy Call
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
